<template>
  <div class="candidate-card-grid">
    <div class="candidate-card-grid__grid">
      <article
        v-for="item in items"
        :key="item.id"
        class="candidate-card"
        role="button"
        tabindex="0"
        @click="$emit('open', item.id)"
        @keydown.enter.prevent="$emit('open', item.id)"
        @keydown.space.prevent="$emit('open', item.id)"
      >
        <!-- Header: nume + profesie (subtitle) + status „Has CV" -->
        <div class="d-flex align-start justify-space-between ga-2">
          <div class="candidate-card__main">
            <div class="candidate-card__title">{{ fullName(item) }}</div>
            <div class="candidate-card__subtitle">{{ item.Profession || ("ID " + item.id) }}</div>
          </div>
          <v-chip
            :color="item.HasCV ? 'success' : 'neutral'"
            class="flex-shrink-0"
          >
            <v-icon start size="13">{{ item.HasCV ? "mdi-file-check-outline" : "mdi-file-remove-outline" }}</v-icon>
            {{ $t("candidates.card.hasCv") }}
          </v-chip>
        </div>

        <!-- Meta: telefon, email, locație -->
        <div class="candidate-card__meta mt-2">
          <div v-if="item.PhoneNumber" class="candidate-card__meta-row">
            <v-icon size="14" class="candidate-card__meta-icon">mdi-phone-outline</v-icon>
            <span>{{ item.PhoneNumber }}</span>
          </div>
          <div v-if="item.Email" class="candidate-card__meta-row">
            <v-icon size="14" class="candidate-card__meta-icon">mdi-email-outline</v-icon>
            <span>{{ item.Email }}</span>
          </div>
          <div v-if="locationLine(item)" class="candidate-card__meta-row">
            <v-icon size="14" class="candidate-card__meta-icon">mdi-map-marker-outline</v-icon>
            <span>{{ locationLine(item) }}</span>
          </div>
        </div>

        <!-- KPI chips: contactat, interesat, sursă CV -->
        <div class="d-flex flex-wrap ga-2 mt-2">
          <v-chip :color="item.CandidateContacted ? 'success' : 'neutral'">
            <v-icon start size="13">{{ item.CandidateContacted ? "mdi-check" : "mdi-close" }}</v-icon>
            {{ $t("candidates.card.contacted") }}
          </v-chip>
          <v-chip :color="item.InterestedInOffers ? 'success' : 'neutral'">
            <v-icon start size="13">{{ item.InterestedInOffers ? "mdi-check" : "mdi-close" }}</v-icon>
            {{ $t("candidates.card.interestedShort") }}
          </v-chip>
          <v-chip v-if="item.CVSource" color="neutral">
            <v-icon start size="13">mdi-file-find-outline</v-icon>
            {{ item.CVSource }}
          </v-chip>
        </div>

        <!-- Footer: data nașterii + acțiuni -->
        <div class="candidate-card__footer mt-auto pt-3">
          <div class="candidate-card__footer-meta">
            <v-icon size="13" class="candidate-card__footer-icon">mdi-cake-variant-outline</v-icon>
            <span class="candidate-card__footer-text">{{ formatDate(item.Dateofbirth) || "—" }}</span>
          </div>
          <div class="candidate-card__actions">
            <v-btn
              icon
              variant="text"
              size="small"
              :title="$t('app.actions.edit')"
              @click.stop="$emit('edit', item)"
            >
              <v-icon size="18" color="primary">mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              :title="$t('app.actions.delete')"
              @click.stop="$emit('delete', item.id)"
            >
              <v-icon size="18" color="error">mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";

// Card de candidat în anatomia de board standard (referință: NcrmContractsHome, docs/design.md):
// titlu (nume) + sub-identificator (profesie) + chip status, meta cu iconițe, KPI chips, footer.
// Single-click pe toată suprafața deschide detaliile; edit/delete au @click.stop.
export default defineComponent({
  name: "CandidateCardGrid",
  props: {
    items: { type: Array, default: () => [] },
  },
  emits: ["open", "edit", "delete"],
  methods: {
    fullName(item) {
      return [item.FirstName, item.LastName].filter(Boolean).join(" ").trim() || "—";
    },
    locationLine(item) {
      return [item.Country, item.Address].filter(Boolean).join(", ");
    },
    formatDate(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      return `${dd}.${mm}.${d.getFullYear()}`;
    },
  },
});
</script>

<style scoped>
.candidate-card-grid__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: stretch;
  justify-content: flex-start;
  padding: 8px 0 16px;
}

.candidate-card {
  flex: 0 1 360px;
  width: min(100%, 360px);
  text-align: left;
  cursor: pointer;
  border: 1px solid var(--ncrm-color-border-default, rgba(var(--v-theme-on-surface), 0.08));
  border-radius: var(--app-radius-base);
  background: rgb(var(--v-theme-surface));
  padding: 16px 16px 14px;
  box-shadow: 0 1px 4px rgba(var(--v-theme-on-surface), 0.05);
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
  display: flex;
  flex-direction: column;
}

.candidate-card:hover,
.candidate-card:focus-visible {
  border-color: var(--ncrm-color-primary-border, rgba(var(--v-theme-primary), 0.4));
  box-shadow: 0 4px 14px rgba(var(--v-theme-on-surface), 0.08);
  transform: translateY(-1px);
  outline: none;
}

.candidate-card__main {
  min-width: 0;
  flex: 1 1 auto;
}

.candidate-card__title {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.25;
  color: var(--app-text-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.candidate-card__subtitle {
  font-size: 0.8rem;
  color: rgba(var(--v-theme-on-surface), 0.48);
  line-height: 1.3;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.candidate-card__meta {
  display: grid;
  gap: 5px;
}

.candidate-card__meta-row {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  color: rgba(var(--v-theme-on-surface), 0.68);
  font-size: 0.88rem;
}

.candidate-card__meta-row span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.candidate-card__meta-icon {
  opacity: 0.65;
  flex-shrink: 0;
}

.candidate-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 5px;
  border-top: 1px solid var(--ncrm-color-border-subtle, rgba(var(--v-theme-on-surface), 0.06));
}

.candidate-card__footer-meta {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}

.candidate-card__footer-icon {
  opacity: 0.32;
  flex-shrink: 0;
}

.candidate-card__footer-text {
  color: rgba(var(--v-theme-on-surface), 0.38);
  font-size: 0.76rem;
}

.candidate-card__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

@media (max-width: 720px) {
  .candidate-card-grid__grid {
    gap: 12px;
  }

  .candidate-card {
    flex-basis: 100%;
    width: 100%;
  }
}
</style>
